/**
 * Roamzy analytics — two layers, both privacy-friendly, both free:
 *
 *   1. Cloudflare Web Analytics for page views.
 *      Activated when `VITE_CF_ANALYTICS_TOKEN` is set. Drops a single
 *      `<script>` tag pointing at static.cloudflareinsights.com which
 *      tracks page views, route changes (SPA-aware), and core web
 *      vitals — without cookies, fingerprinting, or external trackers.
 *      Free tier: unlimited page views.
 *
 *   2. Self-hosted custom-event tracker.
 *      `track('Goal', props)` POSTs to /api/track which writes to our
 *      own SQLite (mig 0016 → table track_events). Admin UI surfaces
 *      day-by-name aggregates (free, no third-party).
 *
 * Both layers no-op silently if their respective config isn't set, so
 * local dev doesn't pollute prod data.
 */
import { getCookieId } from './utm';
import { analyticsAllowed } from './consent';

const CF_ANALYTICS_TOKEN = import.meta.env.VITE_CF_ANALYTICS_TOKEN as string | undefined;

let injected = false;

/**
 * Inject the Cloudflare Web Analytics beacon. Idempotent (second call is
 * no-op). Safe to call before DOMContentLoaded — appends to <head> and
 * the script tag handles its own deferral.
 */
export function bootAnalytics(): void {
  // Consent gate (audit 2026-07-29): the beacon used to load for every visitor
  // before any choice existed, because the consent banner could never be shown.
  if (!analyticsAllowed()) return;
  if (injected || !CF_ANALYTICS_TOKEN) return;
  injected = true;
  try {
    const s = document.createElement('script');
    s.defer = true;
    s.src = 'https://static.cloudflareinsights.com/beacon.min.js';
    s.dataset.cfBeacon = JSON.stringify({ token: CF_ANALYTICS_TOKEN });
    document.head.appendChild(s);
  } catch { /* swallow */ }
}

/**
 * Custom event. Fire-and-forget. Safe to call from any code path —
 * `keepalive: true` lets the request survive a same-tap navigation.
 *
 * Common goals (any string works, but stick to PascalCase by convention):
 *   - 'CtaClick'
 *   - 'TopUpClick'   — visitor opened the topup modal
 *   - 'TopUpStarted' — visitor submitted topup form (pre-redirect)
 *   - 'EsimQrShown'  — visitor opened the QR modal
 *
 * Server-side conversion events (signup, paid IPN) are tracked through
 * /api/exp/event from backend hooks — no need to call this from the
 * client for those.
 */
export function track(name: string, props?: Record<string, string | number | boolean>): void {
  // Our own tracker is the more invasive of the two layers: it mints a
  // permanent id in localStorage and the backend stores it next to the IP and
  // user-agent. No consent, no event, and no id is created either.
  if (!analyticsAllowed()) return;
  try {
    const body: Record<string, unknown> = {
      name,
      cookieId: getCookieId(),
      path: window.location.pathname,
    };
    if (props && Object.keys(props).length > 0) body.props = props;
    fetch('/api/track', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'include',
    }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
}
